/**
 * De plek van de werkbalk (het lint) boven het canvas.
 *
 * De werkbalk hoort bij de canvassen: die kennen de selectie. Hij staat in
 * beeld als vaste balk bovenaan de werkruimte, van de rechterrand van de
 * linkerpanelen tot de rechterrand van het scherm — schuiven de panelen in of
 * uit, dan schuift de balk mee. `RibbonSlot` is die plek (in `Editor.tsx`);
 * een canvas zet z'n werkbalk erin met `RibbonPortal`. Zonder plek (een
 * canvas buiten de editor) staat de werkbalk gewoon waar het canvas 'm
 * tekent.
 *
 * Een canvas zonder werkbalk (roadmap, matrix, verklarende woordenlijst)
 * laat de plek leeg; die neemt dan geen ruimte in.
 */
import { type ReactNode, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

let target: HTMLElement | null = null;
const listeners = new Set<() => void>();

function setTarget(el: HTMLElement | null): void {
  if (target === el) return;
  target = el;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** De plek zelf. Eén keer in de editor, boven de diagram-tabs. */
export function RibbonSlot() {
  return <div ref={setTarget} data-ribbon-slot="true" className="shrink-0 empty:hidden" />;
}

/** Zet de werkbalk in de plek, of laat 'm staan als er geen plek is. */
export function RibbonPortal({ children }: { children: ReactNode }) {
  const el = useSyncExternalStore(
    subscribe,
    () => target,
    () => null,
  );
  return el ? createPortal(children, el) : children;
}
